// externals

// components
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// libraries
import * as accessoriesSelectors from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import * as calculationDetailsSelectors from '@makemydeal/dr-dash-store/selectors/calculationDetailsSelectors';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import type { DealHistory } from '@makemydeal/dr-dash-types';

// types
import type { LineItem } from '../../types/LineItemTypes';

// constants
import * as constants from '../../constants';

// feature toggles
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// utils
import {
    assignFallbackLabels,
    assignVppFallbackLabels,
    convertAccessoryToLineItem,
    convertDealerFeeToLineItem,
    convertGovernmentFeeToLineItem,
    convertVppProductToLineItem,
    getCappedFeeBreakdown,
    getLabelStyles,
    getStrAmountsWithoutSymbols,
    lineItemWithChildrenAdaptor,
    removeTaxesByName
} from '../utils/transform';

// components
import DealSummaryItem from '../common/DealSummaryItem';
import { DealSummaryItemWrapper } from '../common/DealSummaryItemWrapper';
import LineItemWithChildren from '../common/LineItemWithChildren';

import { Grid } from '@interstate/components/Grid';
import * as vehicleProtectionUtils from '@makemydeal/dr-dash-store/utils/vehicleProtectionUtils';
import { useMemo } from 'react';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';

// styles
import {
    defaultGridProps,
    labelStyles as defaultLabelStyles,
    valueStyles as defaultValueStyles,
    detailedViewGridProps,
    detailedViewLabelStyles,
    detailedViewValueStyles,
    expandibleAreaStyles,
    expandibleButtonStyles,
    secondaryValueStyles
} from '../utils/styles';

import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useMediaQuery } from 'react-responsive';
import { useDealSummary } from '../../common/DealSummaryProvider';
import { TaxNames } from '@makemydeal/dr-platform-types';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export type AddsToCapCostProps = {
    deal?: DealHistory;
    useWrapper?: boolean;
    hasDelta?: boolean;
};

const AddsToCapCost = ({ deal, useWrapper = true, hasDelta = false }: AddsToCapCostProps) => {
    const useDealRootSelector = createDealRootSelector(deal, 'AddsToCapCost');
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();
    const initialIsFirstPaymentCapitalized = useTrackedSelector(calculationDetailsSelectors.getIsFirstPaymentCapitalized, {
        component: 'AddsToCapCost',
        field: 'isFirstPaymentCapitalized'
    });
    const paymentFirstPaymentCapitalized = useTrackedSelector(offerReduxSelectors.getFirstPaymentCapitalized, {
        component: 'AddsToCapCost',
        field: 'firstPaymentCapitalized'
    });
    const firstPaymentCapitalized = paymentFirstPaymentCapitalized ?? initialIsFirstPaymentCapitalized;

    const monthlyPayment = useHeldValue(useDealRootSelector(offerSelectors.getMonthlyPayment, 'monthlyPayment'), deal);

    // add to cap cost
    const isCapReductionTaxCapitalizedIndicator = useTrackedSelector(offerReduxSelectors.getCapReductionTaxCapitalizedIndicator, {
        component: 'AddsToCapCost',
        field: 'capReductionTaxCapitalizedIndicator'
    });
    // Two DIFFERENT selectors share the name `getAddsToCapCostTotal`: the
    // composite one sums the capped line items client-side (accessories +
    // capped dealer fees + gov fees + protection, plus capitalized lender fees
    // once US2116905_Enable_Lender_Fees_From_Priced_Term is on), while the offer-redux one is
    // the offer's own authoritative total. They are instrumented under DISTINCT
    // field keys on purpose — the dedup key is `storeId|component|field|
    // instanceKey`, so giving them one key made each read see the other's value
    // as "previous" and both emitted forever (observed: 0 <-> 1005.59 alternating
    // on all 36 SOCKET_EVENT_IGNORED dispatches, 0-11ms apart). Keeping them
    // separate is also what makes the pair comparable: `computed` vs offer-
    // provided divergence IS the cap-cost math-stack check.
    let addsToCapCostTotal = useDealRootSelector(compositeSelectors.getAddsToCapCostTotal, 'addsToCapCostTotalComputed');
    const addsToCapCostTotalAmount = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getAddsToCapCostTotal, {
            component: 'AddsToCapCost',
            field: 'addsToCapCostTotal'
        })
    );

    if (firstPaymentCapitalized) {
        addsToCapCostTotal += monthlyPayment;
    }

    const isLenderFeesFromPricedTermEnabled = useTrackedSelector(featureToggleSelectors.isLenderFeesFromPricedTermEnabled, {
        component: 'AddsToCapCost',
        field: 'lenderFeesFromPricedTerm'
    });

    // acquisition fee
    const acqUpfront = useHeldValue(
        useTrackedSelector(offerSelectors.getAcqFeeUpFrontOverrideWithFallback, {
            component: 'AddsToCapCost',
            field: 'acqFeeUpFrontOverrideWithFallback'
        }),
        deal
    );
    const acqFeeOverride = useHeldValue(
        useTrackedSelector(offerSelectors.getAcqFeeOverrideWithFallback, {
            component: 'AddsToCapCost',
            field: 'acqFeeOverrideWithFallback'
        }),
        deal
    );
    /**
     * The acquisition fee is already inside capitalizedLenderFees, which getAddsToCapCostTotal now
     * counts, so adding it again here would double it. Fee ids 1050/1051 share one lender program
     * field, which is why the same amount arrives twice.
     */
    const acqFee = acqUpfront || isLenderFeesFromPricedTermEnabled ? 0 : acqFeeOverride;
    addsToCapCostTotal += acqFee;

    // accessories
    const accessoriesTotal = useDealRootSelector(accessoriesSelectors.getCapAccessoriesTotal, 'capAccessoriesTotal');
    const capAccessoriesTotal = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapitalizedAccessories, 'capitalizedAccessories'),
        deal
    );
    const rawCapAccessories = useDealRootSelector(accessoriesSelectors.getCapAccessoriesList, 'capAccessoriesList');
    const itemizedAccessories = useMemo(() => {
        const source = assignFallbackLabels(
            rawCapAccessories,
            (acc) => acc.name,
            (acc, label) => ({ ...acc, name: label }),
            constants.ACCESSORY_FALLBACK_LABEL
        );
        return source.map(convertAccessoryToLineItem);
    }, [rawCapAccessories]);
    const itemizedAccessoriesDisplay = lineItemWithChildrenAdaptor(getStrAmountsWithoutSymbols(itemizedAccessories), {
        key: 'label',
        label: 'label',
        value: 'value'
    });

    // taxes
    const capitalizedTaxTotal = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getCapitalizedTaxes, {
            component: 'AddsToCapCost',
            field: 'capitalizedTaxes'
        })
    );
    const hasManualTax = useTrackedSelector(offerReduxSelectors.hasManualTotalTax, {
        component: 'AddsToCapCost',
        field: 'manualTotalTax'
    });
    const getFlatOrItemizedTaxes = useTrackedSelector(offerSelectors.getFlatOrItemizedTaxes, {
        component: 'AddsToCapCost',
        field: 'flatOrItemizedTaxes'
    });
    const shouldDisplayManualTax = hasManualTax && capitalizedTaxTotal !== 0;
    const getManualTax = useTrackedSelector(offerSelectors.getFlatTaxForDisplay, {
        component: 'AddsToCapCost',
        field: 'flatTaxForDisplay'
    });
    const getManualTaxDisplay = lineItemWithChildrenAdaptor(getManualTax, { key: 'taxCode', label: 'taxName', value: 'taxAmount' });
    const isTaxCapped = useTrackedSelector(offerReduxSelectors.isTaxCapped, { component: 'AddsToCapCost', field: 'taxCapped' });

    // Show capitalized taxes: Advanced/Flat when isTaxCapped, CRT when isCapReductionTaxCapitalizedIndicator
    const shouldShowCapitalizedTaxItems = isTaxCapped || isCapReductionTaxCapitalizedIndicator;
    const taxesToRemove = [
        TaxNames.MonthlyUseTax, // Always remove MUT (it's monthly, not capitalized)
        ...getFlatOrItemizedTaxes.filter((tax) => tax.monthlyTax != null).map((tax) => tax.taxName), // Remove any monthly taxes
        ...(!isTaxCapped ? [TaxNames.Flat, TaxNames.Advanced] : []), // Remove Flat/Advanced if NOT capitalized
        ...(!isCapReductionTaxCapitalizedIndicator ? [TaxNames.CapCostReduction] : []) // Remove CRT if NOT capitalized
    ];

    const taxItemsToDisplay = shouldShowCapitalizedTaxItems ? removeTaxesByName(getFlatOrItemizedTaxes, taxesToRemove) : [];
    const capitalizedTaxesDisplay = lineItemWithChildrenAdaptor(taxItemsToDisplay, {
        key: 'taxCode',
        label: 'taxName',
        value: 'taxAmount'
    });
    const itemizedTaxes = shouldDisplayManualTax ? getManualTaxDisplay : capitalizedTaxesDisplay;

    // fees
    const dealerFeeTotal = useDealRootSelector(offerSelectors.getCappedDealerFeesTotal, 'cappedDealerFeesTotal');
    const itemizedDealerFees = useDealRootSelector(offerSelectors.getDealerFees, 'dealerFees');
    const dealerFeeChildren = useMemo(() => {
        const { cappedFees } = getCappedFeeBreakdown(itemizedDealerFees);
        const source = assignFallbackLabels(
            cappedFees,
            (fee) => fee.dealerFeeName,
            (fee, label) => ({ ...fee, dealerFeeName: label }),
            constants.FEE_FALLBACK_LABEL
        );
        return source.map(convertDealerFeeToLineItem);
    }, [itemizedDealerFees]);

    const capitalizedLenderFeesTotal = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getCapitalizedLenderFees, {
            component: 'AddsToCapCost',
            field: 'capitalizedLenderFees'
        })
    );
    const itemizedLenderFees = useTrackedSelector(offerSelectors.getIncludedCappedLenderFees, {
        component: 'AddsToCapCost',
        field: 'includedCappedLenderFees'
    });
    const itemizedLenderFeesDisplay = lineItemWithChildrenAdaptor(itemizedLenderFees, {
        key: 'dealerFeeTypeCode',
        label: 'dealerFeeName',
        value: 'dealerFeeAmount'
    });
    const itemizedLenderFeeChildren = useMemo(() => itemizedLenderFees.map(convertDealerFeeToLineItem), [itemizedLenderFees]);

    const capitalizedGovFees = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getCapitalizedGovFees, {
            component: 'AddsToCapCost',
            field: 'capitalizedGovFees'
        })
    );
    const itemizedGovFees = useTrackedSelector(offerSelectors.getIncludedCappedGovernmentFees, {
        component: 'AddsToCapCost',
        field: 'includedCappedGovernmentFees'
    });
    const itemizedGovFeesDisplay = lineItemWithChildrenAdaptor(itemizedGovFees, {
        key: 'code',
        label: 'name',
        value: 'amount'
    });

    const capitalizedDealerFees = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getCapitalizedDealerFees, {
            component: 'AddsToCapCost',
            field: 'capitalizedDealerFees'
        })
    );
    const dealerFeesItemized = useTrackedSelector(offerSelectors.getIncludedCappedDealerFees, {
        component: 'AddsToCapCost',
        field: 'includedCappedDealerFees'
    });
    const dealerFeesItemizedDisplay = lineItemWithChildrenAdaptor(dealerFeesItemized, {
        key: 'dealerFeeTypeCode',
        label: 'dealerFeeName',
        value: 'dealerFeeAmount'
    });

    const govermentFee = useDealRootSelector(offerReduxSelectors.getGovernmentFees, 'governmentFees');
    const { cappedFeeTotal, fee } = govermentFee;
    const govFeeChildren: LineItem[] = useMemo(() => {
        const filtered = fee.taxFee.filter((f) => f.capped && f.amount > 0);
        const source = assignFallbackLabels(
            filtered,
            (f) => f.name,
            (f, label) => ({ ...f, name: label }),
            constants.FEE_FALLBACK_LABEL
        );
        return source.map(convertGovernmentFeeToLineItem);
    }, [fee]);

    // protection products
    const protectionProductsTotal = useDealRootSelector(vehicleProtectionSelectors.getCapVppTotal, 'capVppTotal');
    const capProtectionProductsTotal = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapitalizedProtectionProducts, 'capitalizedProtectionProducts'),
        deal
    );
    const vppProducts = useDealRootSelector(vehicleProtectionSelectors.getVppProducts, 'vppProducts');
    const protectionProductsItems = useMemo(() => {
        const filtered = vppProducts
            .filter(vehicleProtectionUtils.isVppProductSelected)
            .filter(vehicleProtectionUtils.isVppCapProduct);
        const source = assignVppFallbackLabels(filtered);
        return source.map(convertVppProductToLineItem);
    }, [vppProducts]);
    const protectionProductsDisplay = lineItemWithChildrenAdaptor(getStrAmountsWithoutSymbols(protectionProductsItems), {
        key: 'label',
        label: 'label',
        value: 'value'
    });

    // prior loan balance
    const priorLoanBalance = useHeldValue(
        useTrackedSelector(offerReduxSelectors.getPriorLoanBalance, {
            component: 'AddsToCapCost',
            field: 'priorLoanBalance'
        })
    );
    const isPriorBalanceEnabled = useTrackedSelector(featureToggleSelectors.isPriorBalanceEnabled, {
        component: 'AddsToCapCost',
        field: 'priorBalanceEnabled'
    });
    const capitalizedPriorBalance = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCapitalizedPriorLeaseBalanceByOfferType, 'capitalizedPriorLeaseBalance'),
        deal
    );
    const showCapitalizedPriorBalance = isPriorBalanceEnabled && capitalizedPriorBalance > 0;
    const tradeInAllowance = useTrackedSelector(tradeInSelectors.getTradeValue, {
        component: 'AddsToCapCost',
        field: 'tradeValue'
    });
    const tradeInAmountOwed = useTrackedSelector(tradeInSelectors.getTradeInAmountOwed, {
        component: 'AddsToCapCost',
        field: 'tradeInAmountOwed'
    });
    const tradeInItems = [
        {
            label: constants.ALLOWANCE,
            value: tradeInAllowance,
            key: `${constants.ALLOWANCE}-${tradeInAllowance}`
        },
        {
            label: constants.PAYOFF,
            value: tradeInAmountOwed,
            key: `${constants.PAYOFF}-${tradeInAmountOwed}`
        }
    ];

    const isEnhancedDealDetailsEnabled = useTrackedSelector(featureToggleSelectors.getEnhancedDealDetails, {
        component: 'AddsToCapCost',
        field: 'enhancedDealDetails'
    });

    const labelStyles = showDetailedView ? detailedViewLabelStyles : defaultLabelStyles;
    const valueStyles = showDetailedView ? detailedViewValueStyles : defaultValueStyles;
    const gridProps = showDetailedView && isLargeSize ? detailedViewGridProps : defaultGridProps;

    if (isEnhancedDealDetailsEnabled) {
        return (
            <DealSummaryItem
                valueStyles={valueStyles}
                labelStyles={!showDetailedView ? labelStyles : {}}
                label={constants.ADDS_TO_CAP_COST}
                value={formatDollarsAndCents(addsToCapCostTotalAmount)}
            >
                <Grid container sx={gridProps}>
                    {firstPaymentCapitalized && (
                        <LineItemWithChildren
                            containerStyles={{ py: 1 }}
                            label={constants.FIRST_MONTHLY_PAYMENT}
                            value={formatDollarsAndCents(monthlyPayment)}
                            labelStyles={showDetailedView ? detailedViewLabelStyles : {}}
                            valueStyles={showDetailedView ? secondaryValueStyles : {}}
                        />
                    )}
                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={getLabelStyles(showDetailedView, itemizedTaxes.length > 0)}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                        label={constants.CAPITALIZED_TAXES}
                        value={formatDollarsAndCents(capitalizedTaxTotal)}
                    >
                        {!!itemizedTaxes.length &&
                            itemizedTaxes.map((tax) => (
                                <LineItemWithChildren key={tax.key} label={tax.label} value={formatDollarsAndCents(tax.value)} />
                            ))}
                    </DealSummaryItem>

                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={getLabelStyles(showDetailedView, itemizedLenderFeesDisplay.length > 0)}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                        label={constants.LENDER_FEES}
                        value={formatDollarsAndCents(capitalizedLenderFeesTotal)}
                    >
                        {!!itemizedLenderFeesDisplay.length &&
                            itemizedLenderFeesDisplay.map((fee) => (
                                <LineItemWithChildren key={fee.key} label={fee.label} value={formatDollarsAndCents(fee.value)} />
                            ))}
                    </DealSummaryItem>

                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={getLabelStyles(showDetailedView, itemizedGovFeesDisplay.length > 0)}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                        label={constants.GOVERNMENT_FEES}
                        value={formatDollarsAndCents(capitalizedGovFees)}
                    >
                        {!!itemizedGovFeesDisplay.length &&
                            itemizedGovFeesDisplay.map((fee) => (
                                <LineItemWithChildren key={fee.key} label={fee.label} value={formatDollarsAndCents(fee.value)} />
                            ))}
                    </DealSummaryItem>

                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={getLabelStyles(showDetailedView, dealerFeesItemizedDisplay.length > 0)}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                        label={constants.DEALER_FEES}
                        value={formatDollarsAndCents(capitalizedDealerFees)}
                    >
                        {!!dealerFeesItemizedDisplay.length &&
                            dealerFeesItemizedDisplay.map((fee) => (
                                <LineItemWithChildren key={fee.key} label={fee.label} value={formatDollarsAndCents(fee.value)} />
                            ))}
                    </DealSummaryItem>

                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={getLabelStyles(showDetailedView, protectionProductsDisplay.length > 0)}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                        label={constants.PROTECTION_PRODUCTS}
                        value={formatDollarsAndCents(capProtectionProductsTotal)}
                    >
                        {!!protectionProductsDisplay.length &&
                            protectionProductsDisplay.map((product) => (
                                <LineItemWithChildren
                                    key={product.key}
                                    label={product.label}
                                    value={formatDollarsAndCents(product.value)}
                                />
                            ))}
                    </DealSummaryItem>

                    <DealSummaryItem
                        valueStyles={secondaryValueStyles}
                        labelStyles={getLabelStyles(showDetailedView, itemizedAccessoriesDisplay.length > 0)}
                        expandibleButtonStyles={expandibleButtonStyles}
                        expandibleAreaStyles={expandibleAreaStyles}
                        label={constants.ACCESSORIES}
                        value={formatDollarsAndCents(capAccessoriesTotal)}
                    >
                        {!!itemizedAccessoriesDisplay.length &&
                            itemizedAccessoriesDisplay.map((accessory) => (
                                <LineItemWithChildren
                                    key={accessory.key}
                                    label={accessory.label}
                                    value={formatDollarsAndCents(accessory.value)}
                                />
                            ))}
                    </DealSummaryItem>

                    {showCapitalizedPriorBalance && (
                        <DealSummaryItem
                            valueStyles={secondaryValueStyles}
                            labelStyles={getLabelStyles(showDetailedView, false)}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            label={constants.PRIOR_BALANCE}
                            value={formatDollarsAndCents(capitalizedPriorBalance)}
                        />
                    )}

                    {priorLoanBalance > 0 && (
                        <DealSummaryItem
                            valueStyles={secondaryValueStyles}
                            labelStyles={labelStyles}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            label={constants.NEGATIVE_TRADE_EQUITY}
                            value={formatDollarsAndCents(priorLoanBalance)}
                        >
                            {tradeInItems.map((tradeInItem) => (
                                <LineItemWithChildren
                                    key={tradeInItem.key}
                                    label={tradeInItem.label}
                                    value={formatDollarsAndCents(tradeInItem.value)}
                                />
                            ))}
                        </DealSummaryItem>
                    )}
                </Grid>
            </DealSummaryItem>
        );
    }

    return (
        <DealSummaryItemWrapper
            label={constants.ADDS_TO_CAP_COST}
            value={formatDollarsAndCents(addsToCapCostTotal)}
            useWrapper={useWrapper}
            hasDelta={hasDelta}
        >
            <Grid>
                {firstPaymentCapitalized && (
                    <LineItemWithChildren label={constants.MO_PAYMENT} value={formatDollarsAndCents(monthlyPayment)} />
                )}
                {!isLenderFeesFromPricedTermEnabled && (
                    <LineItemWithChildren label={constants.ACQUISITION_FEE} value={formatDollarsAndCents(acqFee)} />
                )}
                <LineItemWithChildren
                    label={constants.ACCESSORIES}
                    value={formatDollarsAndCents(accessoriesTotal)}
                    childItems={itemizedAccessories}
                />
                <LineItemWithChildren
                    label={constants.DEALER}
                    value={formatDollarsAndCents(dealerFeeTotal)}
                    childItems={dealerFeeChildren}
                />
                {isLenderFeesFromPricedTermEnabled && (
                    <LineItemWithChildren
                        label={constants.LENDER_FEES}
                        value={formatDollarsAndCents(capitalizedLenderFeesTotal)}
                        childItems={itemizedLenderFeeChildren}
                    />
                )}
                <LineItemWithChildren
                    label={constants.GOV_FEES}
                    value={formatDollarsAndCents(cappedFeeTotal)}
                    childItems={govFeeChildren}
                />
                <LineItemWithChildren
                    label={constants.PROTECTION}
                    value={formatDollarsAndCents(protectionProductsTotal)}
                    childItems={protectionProductsItems}
                />
            </Grid>
        </DealSummaryItemWrapper>
    );
};

export default AddsToCapCost;
